<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { checkLogin, LoginResult } from 'JIX/utils/login.js';

import 'icons/tooltip.svg';

import CompanyRatingComment from 'JIX/components/CompanyRatingComment.vue';
import CompanyRatingNew from 'JIX/components/CompanyRatingNew.vue';
import CompanyRatingStars from 'JIX/components/CompanyRatingStars.vue';
import SvgIcon from 'JIX/components/SvgIcon.vue';
import { bsTooltip as vBsTooltip } from 'JIX/vue-bs-tooltip.js';
import GaisScoreGraphic from './CompanyProfileGaisScoreGraphic.vue';
import T from './CompanyProfileEvaluationsTab.i18n.js';

const newEvaluationModal = ref();

const store = useStore();

const detailedRatingsVisible = computed(() => store.state.isAdm || store.state.isCandidateUser);
const companyName = computed(() => store.state.companyName);
const evaluationData = computed(() => store.state.evaluationData);
const urls = computed(() => store.state.urls);
const existing_rating = ref(null);

function jixAnalytics(label, action) {
    store.dispatch('jixAnalytics', { label, action });
}

function loginCheckRate() {
    checkLogin()
        .then(loginResult => {
            if (loginResult !== LoginResult.ABORTED) {
                setTimeout(function(){
                    newEvaluationModal.value.show();
                }, 300);
            }
        });
}

function loginButtonClicked() {
    if (window.jixAnalytics) {
        window.jixAnalytics('event', { category: 'evaluering-kommentar', action: 'click_login', label: 'login' });
    }
    checkLogin({ source: 'company_evaluations_login' })
        .then(loginResult => {
            if (loginResult !== LoginResult.ABORTED) {
                if (window.jixAnalytics) {
                    window.jixAnalytics('event', { category: 'evaluering-kommentar', action: 'successful_login', label: 'login' });
                }
                location.reload();
            } else if (window.jixAnalytics) {
                window.jixAnalytics('event', { category: 'evaluering-kommentar', action: 'aborted_login', label: 'login' });
            }
        });
}

</script>

<template>
    <div>
        <div class="row">
            <div class="col-md-9">
                <div class="bg-surface-default border p-md-4 mb-4 p-3">
                    <div class="d-flex flex-column flex-md-row align-items-center align-items-md-start">
                        <div class="mr-1 d-flex flex-column">
                            <h3>
                                {{ companyName }}
                            </h3>
                            <p v-if="evaluationData.too_few_ratings" class="text-sm">
                                {{ T.tooFewRatingsText() }}.
                                <a
                                    :href="urls.ratings_not_shown"
                                    class="text-sm"
                                    rel="nofollow"
                                >{{ T.ratingTermsLinkText() }}</a>
                            </p>
                            <p v-if="!evaluationData.too_few_ratings && evaluationData.disable_ratings" class="text-sm">
                                {{ T.ratingsDeactivatedText() }}.
                                <a
                                    :href="urls.rating_terms"
                                    class="text-sm"
                                    rel="nofollow"
                                >{{ T.ratingTermsLinkText() }}</a>
                            </p>
                        </div>
                        <div class="mx-auto mr-md-0 d-flex flex-column align-items-center align-items-md-end">
                            <CompanyRatingStars
                                :rating="evaluationData.question2finalrating[0]"
                                size="md"
                                class="mb-3"
                            />
                            <p v-if="!evaluationData.hide_ratings" class="text-sm">
                                {{ T.evaluationCounts(evaluationData.unique_ratings_cnt, evaluationData.comment_count) }}
                            </p>
                        </div>
                    </div>
                    <hr class="mt-4">
                    <div class="row" v-if="detailedRatingsVisible || evaluationData.too_few_ratings || evaluationData.disable_ratings">
                        <div
                            v-for="index in [2,5,4,7,3,6]"
                            :key="index"
                            class="col-md-4 text-sm mt-4 h5 mb-0"
                        >
                            {{ T.categoryName(index) }}
                            <SvgIcon
                                v-bs-tooltip="T.categoryTooltip(index)"
                                name="tooltip"
                                size="inline"
                            />
                            <CompanyRatingStars
                                :rating="evaluationData.question2finalrating[index]"
                                size="sm"
                                class="mb-md-2"
                            />
                        </div>
                    </div>
                    <div v-else class="d-flex w-100 justify-content-center">
                        <div class="d-flex align-items-center flex-column col-lg-8 col-xl-6 text-center">
                            <h3> {{ T.ratingsLoginWallTitle() }} </h3>
                            <small> {{ T.ratingsLoginWallDescription() }} </small>
                            <button
                                type="button"
                                class="btn btn-primary mt-3"
                                @click.prevent="loginButtonClicked"
                            >
                                {{ T.ratingsLoginWallLoginButton() }}
                            </button>
                        </div>
                    </div>
                </div>
                <div v-if="detailedRatingsVisible">
                    <h3 v-if="evaluationData.rating_data.length > 0">
                        {{ T.commentsHeader(evaluationData.rating_data.length) }}
                    </h3>
                    <CompanyRatingComment
                        v-for="(evaluation, index) in evaluationData.rating_data"
                        :key="index"
                        :comment-data="evaluation"
                        :companyname="companyName"
                        :is-adm="store.state.isAdm"
                    />
                </div>
            </div>
            <div class="col-md-3">
                <div class="mb-3 bg-surface-default border p-3">
                    <h5>
                        {{ T.writeEvaluationHeader() }}
                    </h5>
                    <p class="text-sm">
                        {{ T.writeEvaluationExplain(companyName) }}
                    </p>
                    <a
                        @click.prevent="jixAnalytics('evaluer','klik-menu-evalueringer'); loginCheckRate()"
                        class="btn btn-primary w-100 btn-sm"
                    >
                        <span v-if="existing_rating">
                            {{ T.editEvaluationButton() }}
                        </span>
                        <span v-if="existing_rating==false">
                            {{ T.writeEvaluationButton() }}
                        </span>
                    </a>
                </div>
                <div v-if="evaluationData.gais_data !== null" class="mb-3 bg-surface-default border p-0 d-flex">
                    <GaisScoreGraphic
                        :score="evaluationData.gais_data.total_score"
                        class="mx-auto"
                        width="200"
                        height="200"
                    />
                </div>
            </div>
        </div>
        <CompanyRatingNew
            ref="newEvaluationModal"
            :proxycompanyid="evaluationData.proxycompanyid"
            @existing:rating="existing_rating=$event.existing"
        />
    </div>
</template>
